import React, { useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import { usePasswordModal } from "../contexts/PasswordModalContext";
import { resetPassword } from "../Services/APIService";

interface ChangePasswordModalProps {
  userID: number;
}

interface PasswordResponse {
  message?: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ userID }) => {
  const {
    isOpen, closePasswordModal, error, setError,
    success, setSuccess, loading, setLoading,
  } = usePasswordModal();

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isOpen) return null;

  const getStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[@$!%*?&]/.test(pwd)) score++;
    return score;
  };

  const strength = getStrength(newPassword);
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#e74c3c", "#f39c12", "#3498db", "#2ecc71"][strength];

  const handleUpdatePassword = async (): Promise<void> => {
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setError("Min 8 chars with uppercase, lowercase, number & special character");
      return;
    }

    try {
      setLoading(true);
      const userName = localStorage.getItem("userFullName");
      const payload = userName?.toLowerCase() === "superadmin"
        ? { UserName: userName, password: newPassword }
        : { userID, password: newPassword };

      const result = await resetPassword(payload);

      if (!result.isUpdated) {
        throw new Error(result.message || "Failed to update password");
      } else {
        toast.success(result.message || "Password Updated Successfully!");
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => closePasswordModal(), 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');

        .cpw-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(10, 10, 20, 0.75);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          animation: cpw-fadeIn 0.3s ease;
        }
        @keyframes cpw-fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cpw-slideUp { from { opacity: 0; transform: translateY(30px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }

        .cpw-modal {
          font-family: 'Sora', sans-serif;
          background: #f8f9fa;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 40px 36px;
          width: 100%; max-width: 420px;
          position: relative;
          box-shadow: 0 40px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04);
          animation: cpw-slideUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .cpw-glow {
          position: absolute; top: -60px; left: 50%; transform: translateX(-50%);
          width: 200px; height: 200px; border-radius: 50%;
          background: radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%);
          pointer-events: none;
        }

       .cpw-close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgb(115 107 154);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgb(0 0 0);
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}
        .cpw-close:hover { background: rgb(0 0 0); color: white; }

        .cpw-icon-wrap {
          width: 56px; height: 56px; border-radius: 16px;
          background: linear-gradient(135deg, #362f81, #8e86a5);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 20px;
          box-shadow: 0 8px 24px rgba(99,102,241,0.35);
        }

        .cpw-title {
          font-size: 22px; font-weight: 700;
          color: #212529; margin: 0 0 4px;
          letter-spacing: -0.5px;
        }
        .cpw-subtitle {
          font-size: 13px; color: #212529;
          margin: 0 0 28px; font-weight: 400;
        }

        .cpw-field { margin-bottom: 16px; }
        .cpw-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: rgb(0 0 0 / 50%);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 8px;
}
        .cpw-input-wrap {
          position: relative;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px; overflow: hidden;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .cpw-input-wrap:focus-within {
          border-color: rgba(99,102,241,0.6);
          box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
        }
        .cpw-input {
    width: 100%;
    padding: 13px 44px 13px 16px;
    background: #000000d1;
    border: none;
    outline: none;
    color: #ffffff;
    font-size: 14px;
    font-family: 'Sora', sans-serif;
    box-sizing: border-box;
}
        .cpw-input::placeholder { color: rgba(255,255,255,0.2); }
        .cpw-eye {
          position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
          cursor: pointer; color: rgba(255,255,255,0.3);
          font-size: 15px; transition: color 0.2s;
          background: none; border: none; padding: 0;
        }
        .cpw-eye:hover { color: rgba(255,255,255,0.7); }

        .cpw-strength { margin-top: 10px; }
        .cpw-strength-bars {
          display: flex; gap: 4px; margin-bottom: 5px;
        }
        .cpw-bar {
          flex: 1; height: 3px; border-radius: 99px;
          background: rgba(255,255,255,0.08);
          transition: background 0.3s;
        }
        .cpw-strength-label {
          font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
        }

        .cpw-error {
          background: rgba(231,76,60,0.1); border: 1px solid rgba(231,76,60,0.3);
          border-radius: 10px; padding: 10px 14px;
          color: #ff6b6b; font-size: 12.5px; margin-bottom: 16px;
          display: flex; align-items: center; gap: 8px;
        }

        .cpw-btn {
          width: 100%; padding: 14px;
          background: linear-gradient(135deg, #312833 0%, #373199 100%);
          border: none; border-radius: 12px;
          color: #fff; font-size: 14px; font-weight: 600;
          font-family: 'Sora', sans-serif;
          cursor: pointer; margin-top: 8px;
          transition: all 0.2s; letter-spacing: 0.3px;
          box-shadow: 0 4px 20px rgba(99,102,241,0.4);
        }
        .cpw-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 28px rgba(99,102,241,0.5);
        }
        .cpw-btn:active:not(:disabled) { transform: translateY(0); }
        .cpw-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .cpw-spinner {
          display: inline-block; width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white; border-radius: 50%;
          animation: cpw-spin 0.7s linear infinite;
          margin-right: 8px; vertical-align: middle;
        }
        @keyframes cpw-spin { to { transform: rotate(360deg) } }
      `}</style>

      <div className="cpw-overlay">
        <Toaster position="top-center" />
        <div className="cpw-modal">
          <div className="cpw-glow" />

          <button className="cpw-close" onClick={closePasswordModal}>×</button>

          <div className="cpw-icon-wrap">
            <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>

          <h2 className="cpw-title">Change Password</h2>
          <p className="cpw-subtitle">Set a new secure password for your account</p>

          {error && (
            <div className="cpw-error">
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {/* New Password */}
          <div className="cpw-field">
            <label className="cpw-label">New Password</label>
            <div className="cpw-input-wrap">
              <input
                className="cpw-input"
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
              />
              <button className="cpw-eye" onClick={() => setShowNew(!showNew)} type="button">
                {showNew ? "🙈" : "👁"}
              </button>
            </div>
            {newPassword && (
              <div className="cpw-strength">
                <div className="cpw-strength-bars">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="cpw-bar" style={{ background: i <= strength ? strengthColor : undefined }} />
                  ))}
                </div>
                <span className="cpw-strength-label" style={{ color: strengthColor }}>{strengthLabel}</span>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="cpw-field">
            <label className="cpw-label">Confirm Password</label>
            <div className="cpw-input-wrap">
              <input
                className="cpw-input"
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
              <button className="cpw-eye" onClick={() => setShowConfirm(!showConfirm)} type="button">
                {showConfirm ? "🙈" : "👁"}
              </button>
            </div>
            {confirmPassword && newPassword && (
              <div style={{ marginTop: 6, fontSize: 12, color: confirmPassword === newPassword ? "#2ecc71" : "#e74c3c" }}>
                {confirmPassword === newPassword ? "✓ Passwords match" : "✗ Passwords don't match"}
              </div>
            )}
          </div>

          <button className="cpw-btn" onClick={handleUpdatePassword} disabled={loading}>
            {loading && <span className="cpw-spinner" />}
            {loading ? "Updating..." : "Update Password"}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChangePasswordModal;